import { LitElement, html, css } from 'lit'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// API keys — the desk-wide registry of the systems that call /api/external. Step 9
// of the API channel plan adds the specification sections for this page.
//
// A key speaks for a system, not for a person. The desk has no admin tier, so any
// agent sees every key and any agent can revoke one. The server returns the raw key
// one time only, in the create response. It stores a hash, so it cannot show the key
// again.
//
// The key name is caller text. It goes to the ticket thread as "Opened via <name>".
// Render it with a Lit text binding, which escapes it. Never use unsafeHTML here.

// Relative time for the list. The frontend has no helper of this kind: both
// `page-connections.js` and `ticket-thread.js` format an absolute date instead.
const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
const UNITS = [
  ['year', 31536000], ['month', 2592000], ['week', 604800],
  ['day', 86400], ['hour', 3600], ['minute', 60], ['second', 1]
]

class PageApiKeys extends LitElement {
  static properties = {
    _keys: { state: true },
    _loading: { state: true },
    _name: { state: true },
    _creating: { state: true },
    // The one-time block: the DTO of the key that was just made, plus its raw key.
    _created: { state: true }
  }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); color: var(--eggplant-500); }
    h1 { margin: 0 0 6px; font-size: 32px; font-weight: 800; letter-spacing: -0.02em; }
    .sub { color: var(--neutral-600); font-size: 15px; margin-bottom: 28px; max-width: 60ch; }
    .card { background: var(--white); border-radius: var(--radius-card); box-shadow: var(--shadow-card); padding: 24px; }

    .create { display: flex; gap: 10px; align-items: center; flex-wrap: wrap;
              padding-bottom: 20px; border-bottom: 1px solid var(--border-input); }
    input { flex: 1; min-width: 220px; box-sizing: border-box; font: inherit; font-size: 15px;
            padding: 9px 12px; border: 1px solid var(--border-input);
            border-radius: var(--radius-sm); color: var(--eggplant-500); background: var(--white); }
    input:focus { outline: 2px solid var(--sky-500); outline-offset: -1px; }
    button.primary { font: inherit; font-size: 14px; font-weight: 700; cursor: pointer;
                     padding: 10px 18px; border: none; border-radius: var(--radius-sm);
                     background: var(--chartreuse-500); color: var(--chartreuse-text); }
    button.primary:hover { background: var(--chartreuse-600); }
    button.primary:disabled { opacity: 0.5; cursor: default; }

    .fresh { margin-top: 20px; padding: 16px 18px; background: var(--chartreuse-bg);
             border-left: 4px solid var(--chartreuse-500); border-radius: var(--radius-sm); }
    .fresh .made { font-size: 13.5px; color: var(--neutral-700); margin-bottom: 10px; }
    .fresh .keyline { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
    .fresh .warn { font-size: 13.5px; font-weight: 700; margin-top: 10px; }
    .mono { font-family: ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace; }
    .raw { flex: 1; min-width: 260px; font-size: 14px; word-break: break-all;
           background: var(--white); border: 1px solid var(--chartreuse-300);
           border-radius: var(--radius-sm); padding: 9px 12px; }

    .row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
           padding: 16px 0; border-top: 1px solid var(--border-input); }
    .row:first-of-type { border-top: none; }
    .list .row:first-of-type { border-top: 1px solid var(--border-input); }
    .name { font-size: 16px; font-weight: 700; overflow-wrap: anywhere; }
    .meta { color: var(--neutral-600); font-size: 13.5px; margin-top: 3px; }
    .prefix { font-size: 13.5px; }
    .status { font-size: 13.5px; margin-top: 3px; font-weight: 700; color: var(--sky-500); }
    .status.revoked { color: #B4232B; font-weight: 400; }
    .empty { color: var(--neutral-600); font-size: 15px; padding-top: 16px; }
    button.link { font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; padding: 0; border: none;
                  background: none; color: var(--sky-500); text-decoration: underline; text-underline-offset: 3px; }
    button.link:hover { color: #B4232B; }
  `

  constructor() {
    super()
    this._keys = null
    this._loading = false
    this._name = ''
    this._creating = false
    this._created = null
  }

  connectedCallback() {
    super.connectedCallback()
    this._load()
  }

  // Remove the raw key when the page goes away. Lit keeps the state of a component
  // that is detached and attached again, so the block must be cleared here.
  disconnectedCallback() {
    super.disconnectedCallback()
    this._created = null
  }

  async _load() {
    this._loading = true
    try {
      const { keys } = await api.get('/api-keys')
      this._keys = keys
    } catch (err) {
      toast(err.message || 'Failed to load API keys', 'error')
    } finally {
      this._loading = false
    }
  }

  async _create() {
    const name = this._name.trim()
    if (!name || this._creating) return
    this._creating = true
    try {
      // The response carries `key`, the raw key. Keep it in memory only, and show it
      // one time. The server cannot send it again.
      this._created = await api.post('/api-keys', { name })
      this._name = ''
      toast('Key created', 'success')
      await this._load()
    } catch (err) {
      toast(err.message || 'Failed to create the key', 'error')
    } finally {
      this._creating = false
    }
  }

  async _copy() {
    try {
      // The clipboard needs a secure context. A refusal throws, so tell the agent.
      await navigator.clipboard.writeText(this._created.key)
      toast('Key copied to the clipboard', 'success')
    } catch {
      toast('Could not copy the key. Select it and copy it by hand.', 'error')
    }
  }

  async _revoke(key) {
    if (!confirm(`Revoke “${key.name}”? A system that uses this key stops working at once.`)) return
    try {
      await api.delete('/api-keys/' + encodeURIComponent(key.id))
      toast('Key revoked', 'success')
      await this._load()
    } catch (err) {
      toast(err.message || 'Failed to revoke the key', 'error')
    }
  }

  // "3 hours ago". Return null when there is no timestamp; each caller writes its own
  // words for that case.
  _ago(iso) {
    if (!iso) return null
    const seconds = (new Date(iso).getTime() - Date.now()) / 1000
    for (const [unit, size] of UNITS) {
      if (Math.abs(seconds) >= size || unit === 'second') {
        return RELATIVE.format(Math.round(seconds / size), unit)
      }
    }
    return null
  }

  _exact(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  }

  // `createdBy` and `revokedBy` are names, and either one can be null. The list join
  // gives a null only when that agent's user row was deleted; the create and revoke
  // responses also give a null for an archived agent. A null name is normal. Drop the
  // "by …" words and keep the row.
  _by(name, when) {
    const ago = this._ago(when)
    return name ? `by ${name}, ${ago}` : ago
  }

  _renderRow(k) {
    return html`
      <div class="row">
        <div>
          <div class="name">${k.name}</div>
          <div class="meta prefix"><span class="mono">${k.prefix}</span> · ${k.scopes.join(', ')}</div>
          <div class="meta" title=${this._exact(k.createdAt)}>Created ${this._by(k.createdBy, k.createdAt)}</div>
          <div class="meta">Last used ${this._ago(k.lastUsedAt) || 'never'}</div>
          ${k.active
            ? html`<div class="status">Active</div>`
            : html`<div class="status revoked" title=${this._exact(k.revokedAt)}>
                Revoked ${this._by(k.revokedBy, k.revokedAt)}</div>`}
        </div>
        ${k.active ? html`<button class="link" @click=${() => this._revoke(k)}>Revoke</button>` : ''}
      </div>`
  }

  render() {
    return html`
      <h1>API keys</h1>
      <p class="sub">A key lets an outside system — a SAM install, for example — open tickets on
        this desk. Keys belong to the desk, so every agent sees them and any agent can revoke
        one. Support shows a new key one time, when you make it.</p>
      <div class="card">
        <div class="create">
          <input
            type="text"
            placeholder="Name this key, e.g. “SAM — brandextract.com”"
            aria-label="Key name"
            .value=${this._name}
            @input=${e => { this._name = e.target.value }}
            @keydown=${e => { if (e.key === 'Enter') this._create() }} />
          <button class="primary" ?disabled=${this._creating || !this._name.trim()}
            @click=${this._create}>Create key</button>
        </div>

        ${this._created ? html`
          <div class="fresh">
            <div class="made">New key for <strong>${this._created.name}</strong></div>
            <div class="keyline">
              <span class="raw mono">${this._created.key}</span>
              <button class="primary" @click=${this._copy}>Copy</button>
            </div>
            <div class="warn">Copy it now. It will not be shown again.</div>
          </div>` : ''}

        <div class="list">
          ${this._loading && !this._keys ? html`<div class="empty">Loading…</div>`
            : !this._keys?.length
              ? html`<div class="empty">No keys yet. Make one for the system that opens the tickets.</div>`
              : this._keys.map(k => this._renderRow(k))}
        </div>
      </div>`
  }
}

customElements.define('page-api-keys', PageApiKeys)
